/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';
import { UserMessageModal } from '../../../components/message-modal/MessageModal';
import { UserTableUser } from '../UserTable';
import { UserRoles } from '../../../constants/user';
import { useAppSelector } from '../../../reducers';
import { isOrgManager, isOrgOwner } from '../../../selectors/user';
import { removeOnboarding, removeUser, updateUserPermission } from '../../../actions/initiative';
import { AnalyticsEvents } from '../../../services/analytics/AnalyticsEvents';
import { getAnalytics } from '../../../services/analytics/AnalyticsService';
import { addSiteAlert, SiteAlertColors } from '../../../slice/siteAlertsSlice';
import { useDispatch } from 'react-redux';
import { BlockingLoader } from '../../../components/loader/BlockingLoader';
import { loadCurrentUser } from '../../../actions/user';
import { getRootOrg } from '../../../selectors/initiative';
import SimpleTooltip from '@components/simple-tooltip';

interface UserTableMenuButtonProps {
  user: UserTableUser;
  initiativeId: string;
  handleReload: () => Promise<void>;
  hasOwner: boolean;
}

export const UserTableMenuButton = (props: UserTableMenuButtonProps) => {
  const { user, initiativeId, handleReload, hasOwner } = props;

  const [messageUserId, setMessageUser] = useState<string | undefined>();
  const [isSaving, setSaving] = useState(false);

  const dispatch = useDispatch();

  const rootOrg = useAppSelector(getRootOrg);
  const isCurrentUserOwner = useAppSelector(isOrgOwner);
  const isCurrentUserManager = useAppSelector(isOrgManager);
  const isUserOwner = user.roles.includes(UserRoles.Owner);

  const isRootOrg = rootOrg?._id === initiativeId;
  const anyManagerCanSetOwner = !hasOwner && isCurrentUserManager;
  const canSetOwner = isRootOrg && (anyManagerCanSetOwner || (isCurrentUserOwner && !isUserOwner));

  const handleRemoveUser = async (user: UserTableUser) => {
    setSaving(true);
    try {
      if (user.isActivated) {
        await removeUser(initiativeId, user._id);
      } else {
        await removeOnboarding(initiativeId, user._id);
        const analytics = getAnalytics();
        await analytics.track(AnalyticsEvents.UserInvitationRevoked, {
          initiativeId,
          onboardId: user._id,
        });
      }
      await handleReload();
      dispatch(addSiteAlert({
        content: `User ${user.name} removed.`,
        color: SiteAlertColors.Info,
        timeout: 5000
      }));
      setSaving(false);
    }
    catch (e) {
      dispatch(addSiteAlert({ content: e, timeout: 5000 }));
      setSaving(false);
    }
  }

  const handleSetOwner = async (user: UserTableUser) => {
    if (!canSetOwner) {
      return;
    }
    setSaving(true);
    try {
      const ownerRoles = [
        UserRoles.Owner,
        UserRoles.Manager,
        UserRoles.Contributor,
        UserRoles.Verifier
      ]
      await updateUserPermission(initiativeId, user._id, ownerRoles);
      dispatch(loadCurrentUser(true));
      await handleReload();
      dispatch(addSiteAlert({
        content: `Ownership transferred from you to ${user.name}.`,
        color: SiteAlertColors.Info,
        timeout: 5000
      }));
      setSaving(false);
    }
    catch (e) {
      dispatch(addSiteAlert({ content: e, timeout: 5000 }));
      setSaving(false);
    }
  }

  if (!user.isActivated) {
    return (
      <UncontrolledDropdown>
        <DropdownToggle color='transparent'>
          <i className='fas fa-ellipsis text-ThemeAccentMedium' />
        </DropdownToggle>
        <DropdownMenu>
          <DropdownItem size='sm' onClick={() => handleRemoveUser(user)}>
            <i className='fa fa-times mr-2 text-ThemeDangerMedium' />Revoke invitation
          </DropdownItem>
        </DropdownMenu>
      </UncontrolledDropdown>
    );
  }

  const isOwner = user.roles.includes(UserRoles.Owner);

  return (
    <>
      {isSaving ? <BlockingLoader /> : null}
      {messageUserId ? <UserMessageModal toggle={() => setMessageUser(undefined)} userIds={[messageUserId]} /> : null}
      <UncontrolledDropdown>
        <DropdownToggle color='transparent'>
          <i className='fas fa-ellipsis text-ThemeAccentMedium' />
        </DropdownToggle>
        <DropdownMenu>
          {canSetOwner ?
            <DropdownItem size='sm' onClick={() => handleSetOwner(user)}>
              <i className='fa fa-house-user mr-2' />Set as owner
            </DropdownItem>
            : null}
          <DropdownItem size='sm' onClick={() => setMessageUser(user._id)}>
            <i className='fa fa-envelope mr-2' />Message user
          </DropdownItem>
          <SimpleTooltip text={isOwner ? 'Cannot remove Owner' : ''}>
            <DropdownItem size='sm' onClick={() => handleRemoveUser(user)} disabled={isOwner}>
              <i className='fa fa-times mr-2 text-ThemeDangerMedium' />
              Remove user
            </DropdownItem>
          </SimpleTooltip>
        </DropdownMenu>
      </UncontrolledDropdown>
    </>
  )
}
